import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebRTC } from '../context/WebRTCContext';
import { logAction } from './ActionAuditLog';
import './QuickPunishment.css';

/**
 * Quick punishment actions with context-aware pre-filling.
 * Each action sends a feature message to the desktop for immediate execution,
 * OR navigates to the relevant view pre-filled.
 *
 * Two fields make the consequences legible to non-technical operators:
 *   - `message` actions run INSTANTLY on the subject's real machine; `route`
 *     actions just open a page (nothing happens to them yet). We surface this
 *     as "Runs now" vs "Opens page" so a click's blast radius is obvious.
 *   - `reversible` marks whether a run-now action can be undone/stopped
 *     afterward (unblock, stop sabotage, cancel a timer) or is a one-shot that
 *     can't be taken back (a shock already delivered, a window already killed).
 */
export const ACTIONS = [
    {
        id: 'shock',
        icon: 'iconsminds-flash-2',
        label: 'Send Shock',
        desc: 'Trigger PiShock device',
        severity: 'critical',
        reversible: false,
        key: '1',
        featureId: 'pishock',
        message: { type: 'trigger_shock', intensity: 50, duration: 1 },
    },
    {
        id: 'block_current',
        icon: 'simple-icon-ban',
        label: 'Block Current App',
        desc: 'Block whatever they\'re using now',
        severity: 'high',
        reversible: true,
        key: '2',
        featureId: 'time-limit',
        message: { type: 'block_current_app' },
    },
    {
        id: 'force_close',
        icon: 'simple-icon-close',
        label: 'Force Close Window',
        desc: 'Kill the focused window',
        severity: 'high',
        reversible: false,
        key: '3',
        featureId: 'window-control',
        message: { type: 'close_focused_window' },
    },
    {
        id: 'spin_wheel',
        icon: 'iconsminds-refresh',
        label: 'Spin the Wheel',
        desc: 'Random punishment roulette',
        severity: 'medium',
        reversible: false,
        key: '4',
        featureId: 'spin-wheel',
        message: { type: 'trigger_spin' },
    },
    {
        id: 'sabotage',
        icon: 'simple-icon-cursor',
        label: 'Sabotage Input',
        desc: 'Disrupt keyboard & mouse',
        severity: 'medium',
        reversible: true,
        key: '5',
        featureId: 'input-sabotage',
        message: { type: 'start_sabotage' },
    },
    {
        id: 'shuffle_windows',
        icon: 'simple-icon-grid',
        label: 'Shuffle Windows',
        desc: 'Randomize all window positions',
        severity: 'medium',
        reversible: false,
        key: '6',
        featureId: 'window-rearrange',
        message: { type: 'shuffle_all' },
    },
    {
        id: 'countdown_5min',
        icon: 'iconsminds-timer',
        label: '5min Countdown',
        desc: 'Start a 5-minute countdown timer',
        severity: 'medium',
        reversible: true,
        key: '7',
        featureId: 'countdown-timer',
        message: { type: 'start', seconds: 300, label: 'TIME REMAINING', zeroAction: 'message' },
    },
    {
        id: 'quick_confession',
        icon: 'simple-icon-speech',
        label: 'Quick Confession',
        desc: '"What are you doing right now?"',
        severity: 'medium',
        reversible: true,
        key: '8',
        featureId: 'quick-confession',
        message: { type: 'ask_question', question: 'What are you doing right now?' },
    },
    {   id: 'sep1', separator: true },
    {
        id: 'write_lines',
        icon: 'simple-icon-pencil',
        label: 'Assign Write Lines',
        desc: '"I will not..." repeated',
        severity: 'low',
        key: '9',
        route: '/app/punishment/write-lines',
    },
    {
        id: 'interrogate',
        icon: 'simple-icon-question',
        label: 'Start Interrogation',
        desc: 'Question about current activity',
        severity: 'low',
        route: '/app/communication/interrogation',
    },
    {
        id: 'send_message',
        icon: 'simple-icon-bubble',
        label: 'Send Message',
        desc: 'Quick chat without leaving view',
        severity: 'low',
        route: '/app/communication/chat',
    },
];

const QuickPunishment = () => {
    const navigate = useNavigate();
    const { featureManager, isConnected, connectedPeers } = useWebRTC();
    const [isOpen, setIsOpen] = useState(false);
    const [currentApp, setCurrentApp] = useState(null);
    const menuRef = useRef(null);

    const hasDevice = connectedPeers && Object.keys(connectedPeers).length > 0;

    // Track current app for context
    useEffect(() => {
        if (!featureManager || !isConnected) return;
        const sacredFeature = featureManager.getFeature('sacred-timeline');
        if (!sacredFeature) return;

        const unsubLive = sacredFeature.on('sacred_event', (eventData) => {
            const event = eventData.payload || eventData;
            if (!event || !event.type) return;
            if (event.type === 'activity.app_focused' || event.type === 'activity.app_launched') {
                const d = event.data || event;
                setCurrentApp(d.processName || d.appName || null);
            }
        });

        return () => unsubLive();
    }, [featureManager, isConnected]);

    // Keyboard shortcut: Ctrl+Shift+P to toggle
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.ctrlKey && e.shiftKey && e.key === 'P') {
                e.preventDefault();
                setIsOpen(prev => !prev);
                return;
            }

            // Number keys for quick selection when menu is open
            if (isOpen && e.key >= '1' && e.key <= '9') {
                const action = ACTIONS.find(a => a.key === e.key && !a.separator);
                if (action) {
                    e.preventDefault();
                    executeAction(action);
                }
            }

            if (isOpen && e.key === 'Escape') {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    const executeAction = useCallback((action) => {
        if (action.route) {
            navigate(action.route);
            setIsOpen(false);
            return;
        }

        if (action.featureId && action.message && featureManager) {
            const feature = featureManager.getFeature(action.featureId);
            if (feature && feature.sendMessage) {
                feature.sendMessage(action.message);
                logAction({
                    label: action.label,
                    icon: action.icon,
                    featureId: action.featureId,
                    messageType: action.message.type,
                });
            }
        }

        setIsOpen(false);
    }, [featureManager, navigate]);

    if (!hasDevice) return null;

    return (
        <>
            {/* Backdrop */}
            {isOpen && (
                <div className="qp-backdrop" onClick={() => setIsOpen(false)} />
            )}

            {/* Menu */}
            {isOpen && (
                <div className="qp-menu" ref={menuRef}>
                    <div className="qp-header">
                        <div className="qp-header-title">Quick Actions</div>
                        {currentApp && (
                            <div className="qp-header-context">
                                Subject using: {currentApp}
                            </div>
                        )}
                    </div>

                    <div className="qp-actions">
                        {ACTIONS.map(action => {
                            if (action.separator) {
                                return <div key={action.id} className="qp-separator" />;
                            }

                            return (
                                <button
                                    key={action.id}
                                    className="qp-action"
                                    onClick={() => executeAction(action)}
                                >
                                    <div className={`qp-action-icon severity-${action.severity}`}>
                                        <i className={action.icon} />
                                    </div>
                                    <div className="qp-action-text">
                                        <div className="qp-action-label">{action.label}</div>
                                        <div className="qp-action-desc">{action.desc}</div>
                                        <div className="qp-action-meta">
                                            {action.route ? (
                                                <span className="qp-tag qp-tag-open" data-tooltip="Opens a page — nothing happens to the subject yet">Opens page</span>
                                            ) : (
                                                <>
                                                    <span className="qp-tag qp-tag-run" data-tooltip="Runs immediately on the subject's machine">Runs now</span>
                                                    <span
                                                        className={`qp-tag ${action.reversible ? 'qp-tag-reversible' : 'qp-tag-permanent'}`}
                                                        data-tooltip={action.reversible
                                                            ? 'Can be stopped or undone afterward'
                                                            : "One-shot — can't be taken back"}
                                                    >
                                                        {action.reversible ? 'Undoable' : "Can't undo"}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {action.key && <span className="qp-action-kbd">{action.key}</span>}
                                </button>
                            );
                        })}
                    </div>

                    <div className="qp-footer">
                        <span className="qp-footer-hint">
                            Press <kbd>1</kbd>-<kbd>8</kbd> or click
                        </span>
                    </div>
                </div>
            )}

            {/* Trigger Button */}
            <button
                className={`qp-trigger ${isOpen ? 'is-open' : ''}`}
                onClick={() => setIsOpen(!isOpen)}
                data-tooltip="Quick Actions (Ctrl+Shift+P)"
            >
                {isOpen ? '+' : <i className="iconsminds-flash-2" />}
            </button>
        </>
    );
};

export default QuickPunishment;
